import { useUpdateAgentConfigurationVersion } from '@/queries/agent-configuration-versions';
import { logger } from '@/utils';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import Button from 'antd/es/button';
import Form from 'antd/es/form';
import message from 'antd/es/message';
import Select from 'antd/es/select';
import Switch from 'antd/es/switch';
import Typography from 'antd/es/typography';
import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';

const { Text } = Typography;

interface LLMConfigSectionProps {
  config: {
    llmConfig?: {
      thought_llm?: {
        provider?: string;
        model_tier?: string;
        enable_reasoning?: boolean;
      };
      fact_extraction_llm?: {
        provider?: string;
        model_tier?: string;
        enable_reasoning?: boolean;
      };
      planner_llm?: {
        provider?: string;
        model_tier?: string;
        enable_reasoning?: boolean;
      };
    };
  };
  versionId?: string;
}

export const LLMConfigSection: React.FC<LLMConfigSectionProps> = ({
  config,
  versionId,
}) => {
  const [form] = Form.useForm();
  const updateVersionMutation = useUpdateAgentConfigurationVersion();
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const theme = useTheme();
  // Initialize form values
  useEffect(() => {
    form.setFieldsValue({
      llmConfig: {
        thought_llm: {
          provider: config.llmConfig?.thought_llm?.provider || 'anthropic',
          model_tier: config.llmConfig?.thought_llm?.model_tier || 'large',
          enable_reasoning:
            config.llmConfig?.thought_llm?.enable_reasoning || false,
        },
        fact_extraction_llm: {
          provider:
            config.llmConfig?.fact_extraction_llm?.provider || 'vertexai',
          model_tier:
            config.llmConfig?.fact_extraction_llm?.model_tier || 'large',
          enable_reasoning:
            config.llmConfig?.fact_extraction_llm?.enable_reasoning || false,
        },
        planner_llm: {
          provider: config.llmConfig?.planner_llm?.provider || 'vertexai',
          model_tier: config.llmConfig?.planner_llm?.model_tier || 'large',
          enable_reasoning:
            config.llmConfig?.planner_llm?.enable_reasoning || false,
        },
      },
    });
    setHasChanges(false);
  }, [config, form]);

  const handleFormChange = useCallback(() => {
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!versionId || !hasChanges) return;

    setIsSaving(true);
    try {
      const values = await form.validateFields();

      await updateVersionMutation.mutateAsync({
        versionId,
        data: { llmConfig: values.llmConfig },
      });

      void message.success('LLM configuration saved successfully');
      setHasChanges(false);
    } catch (error) {
      logger.error('Failed to save LLM configuration:', error);
      void message.error('Failed to save LLM configuration');
    } finally {
      setIsSaving(false);
    }
  }, [versionId, hasChanges, form, updateVersionMutation]);

  const renderLLMConfig = (
    llmType: 'thought_llm' | 'fact_extraction_llm' | 'planner_llm',
    title: string,
  ) => (
    <div key={llmType} style={{ marginBottom: `${theme.marginXL}px` }}>
      <Text
        strong
        style={{
          fontSize: `${theme.fontSize}px`,
          color: '#595959',
          marginBottom: `${theme.marginSM}px`,
          display: 'block',
        }}
      >
        {title}
      </Text>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `1fr ${theme.controlHeightLG * 3}px auto`,
          gap: `${theme.marginSM}px`,
          alignItems: 'end',
        }}
      >
        <Form.Item
          name={['llmConfig', llmType, 'provider']}
          label="Provider"
          style={{ margin: 0 }}
        >
          <Select placeholder="Provider">
            <Select.Option value="anthropic">Anthropic</Select.Option>
            <Select.Option value="openai">OpenAI</Select.Option>
            <Select.Option value="vertexai">VertexAI</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name={['llmConfig', llmType, 'model_tier']}
          label="Size"
          style={{ margin: 0 }}
        >
          <Select placeholder="Size">
            <Select.Option value="large">Large</Select.Option>
            <Select.Option value="medium">Medium</Select.Option>
            <Select.Option value="small">Small</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name={['llmConfig', llmType, 'enable_reasoning']}
          label="Reasoning"
          valuePropName="checked"
          style={{ margin: 0 }}
        >
          <Switch />
        </Form.Item>
      </div>
    </div>
  );

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        size="small"
        onFieldsChange={handleFormChange}
      >
        {renderLLMConfig('thought_llm', 'Thought & Reasoning')}
        {renderLLMConfig('fact_extraction_llm', 'Fact Extraction')}
        {renderLLMConfig('planner_llm', 'Planning & Orchestration')}
      </Form>

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          padding: `${theme.padding}px 0`,
          borderTop: `${theme.lineWidth}px solid #f0f0f0`,
          marginTop: `${theme.marginLG}px`,
        }}
      >
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={isSaving}
          disabled={!versionId || !hasChanges}
          block
        >
          Save Changes
        </Button>
      </div>
    </>
  );
};
